import React from "react";
import CombinationResult from "../CombinationResult";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import Pill from "components/Pill";
import { STATUS, UI_THEME } from "utils/constants/ui";
import {
  completeCombination,
  isCombinationResultEmpty,
  prepareCombinationSelectionLabels
} from "utils/combination";
import HoverIconButton from "components/HoverIconButton";
import ProgressBar from "components/ProgressBar";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import { COLORS } from "utils/styles";
import ConfirmIconButton from "components/ConfirmIconButton";
import { COMBINATION_CLEAR } from "utils/constants/confirm";
import PhaseList from "components/PhaseList";
import { useCurrentFormFieldSelection } from "utils/hooks/manifest";
import { hasAnyFormFieldSelections } from "utils/manifest";

const getCombinationsProgressProps = (combinations) => {
  const total = combinations.length;
  const completedCombos = combinations.reduce((memo, combo) => {
    if (!isCombinationResultEmpty(combo.result)) {
      memo += 1;
    }
    return memo;
  }, 0);

  const progress = (completedCombos / total) * 100;
  return {
    progress,
    customClasses: {
      container: "w-100 relative"
    },
    customStyles: {
      progressBarColor: COLORS.blue,
      backgroundColor: COLORS.whitesmoke
    }
  };
};

const EYE = "eye";
const EYE_CLOSED = "eyeClosed";
const PREVIEW_ICONS = {
  EYE,
  EYE_CLOSED
};

const CombinationPreview = ({
  name,
  combination,
  onClick,
  fieldGroups,
  cloneResult,
  clearResult,
  setPreview,
  clearPreview
}) => {
  const result = combination.result;
  const { setFieldValue, values } = useFormikContext();
  const currentSelection = useCurrentFormFieldSelection();

  const hasSelections = hasAnyFormFieldSelections(currentSelection);
  const emptyCombinationCheckout = isCombinationResultEmpty(result);
  const isComplete = completeCombination(combination);
  const hasCheckoutValues = !emptyCombinationCheckout;

  const active = isEqual(currentSelection, combination.selections);

  const activeIndex = get(values, STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX);
  const setActiveIndex = (index) => {
    setFieldValue(STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX, index);
  };
  const configCombinationsPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_COMBOS}`;
  const combinations = get(values, configCombinationsPath, []);
  const total = combinations.length;
  const hasNavControls = combinations.length > 1;

  const progressProps = getCombinationsProgressProps(combinations);

  const selectionLabels = prepareCombinationSelectionLabels({
    combination,
    fieldGroups
  });

  /**
   * Preview states
   */
  let previewProps = {};
  if (isComplete) {
    if (active) {
      previewProps.onClick = clearPreview;
      previewProps.tooltip = "Clear preview";
      previewProps.type = PREVIEW_ICONS.EYE_CLOSED;
      previewProps.color = COLORS.blue;
    } else {
      previewProps.onClick = setPreview;
      previewProps.tooltip = "Preview";
      previewProps.type = PREVIEW_ICONS.EYE;
    }
  } else if (!isComplete && hasSelections) {
    previewProps.onClick = clearPreview;
    previewProps.type = PREVIEW_ICONS.EYE_CLOSED;
    previewProps.tooltip = "Clear fields";
  } else {
    previewProps.disabled = true;
    previewProps.type = PREVIEW_ICONS.EYE;
  }

  return (
    <div className="w-100">
      <ProgressBar {...progressProps} />
      <div className="flex flex-row justify-between ph1 bb hairline-1 pv1">
        <div className="flex w-third items-center justify-start">
          {hasNavControls && (
            <div className="pr2">
              <HoverIconButton
                icon="back"
                onClick={() => {
                  const prevIndex =
                    activeIndex === 0 ? total - 1 : activeIndex - 1;
                  setActiveIndex(prevIndex);
                }}
              />
            </div>
          )}
          <HoverIconButton
            type="copy"
            tooltip="Copy previous"
            onClick={cloneResult}
            disabled={!cloneResult}
          />
          <div className="pl2">
            <HoverIconButton {...previewProps} />
          </div>
        </div>
        <div className="flex w-third items-center justify-center">
          <div className="f7 fw5 black-60">{`${
            activeIndex + 1
          } of ${total}`}</div>
        </div>
        <div className="flex w-third items-center justify-end">
          <div className="dib">
            <ConfirmIconButton
              key={`clear-${hasCheckoutValues}`}
              icon="refresh"
              tooltip="Clear"
              slides={COMBINATION_CLEAR}
              onConfirm={clearResult}
              disabled={!clearResult}
            />
          </div>
          {hasNavControls && (
            <div className="pl2">
              <HoverIconButton
                icon="forward"
                onClick={() => {
                  const nextIndex =
                    activeIndex === total - 1 ? 0 : activeIndex + 1;
                  setActiveIndex(nextIndex);
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div
        onClick={onClick}
        className="flex flex-column w-100 pa3 bb hairline-1 relative pointer relative bg-white--hover bh--inset--hover"
      >
        <div className="flex flex-row justify-between items-center pb2">
          <div className="f5 fw6">When</div>
          <Pill
            theme={UI_THEME.SLIM}
            status={hasCheckoutValues ? STATUS.SUCCESS : STATUS.WARNING}
            copy={hasCheckoutValues ? "Complete" : "Incomplete"}
          />
        </div>
        <div className="pv2">
          <PhaseList phases={selectionLabels} clamp />
        </div>
        <div className="f5 fw6 pv2">Then</div>
        <div className="pt2">
          <CombinationResult {...combination} />
        </div>
      </div>
    </div>
  );
};

export default CombinationPreview;
